<template>

  <v-app>

    <v-main class="main">

      <!-- Sidebar -->
      <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />


        <!-- Page Header -->
        <div class="page-header mt-6">

          <div class="d-flex align-center ga-2">

            <v-btn icon variant="text" @click="goBack">
              <v-icon>
                mdi-arrow-left
              </v-icon>
            </v-btn>

            <div>

              <h2 class="page-title">
                Campaign Referrers
              </h2>

              <p class="page-subtitle">
                Manage referral rewards and campaign payments
              </p>

            </div>

          </div>

        </div>


        <!-- Campaign Summary -->
        <v-card v-if="campaign" class="summary-card mt-5">

          <div class="summary-left">

            <div class="campaign-image-wrapper">

              <img v-if="campaign.image" :src="campaign.image" class="campaign-image" />

              <div v-else class="campaign-image-placeholder">
                <v-icon>
                  mdi-bullhorn-outline
                </v-icon>
              </div>

            </div>


            <div>

              <div class="campaign-name">
                {{ campaign.name }}
              </div>

              <div class="campaign-description">
                {{ campaign.description || 'No description' }}
              </div>

            </div>

          </div>


          <div class="summary-stats">

            <div class="summary-stat">

              <span class="summary-label">
                Total Referrers
              </span>

              <strong>
                {{ refes.length }}
              </strong>

            </div>


            <div class="summary-stat">

              <span class="summary-label">
                Total Referrals
              </span>

              <strong>
                {{ totalReferrals }}
              </strong>

            </div>


            <div class="summary-stat">

              <span class="summary-label">
                Total Reward
              </span>

              <strong>
                ₦{{ formatAmount(totalReward) }}
              </strong>

            </div>

          </div>

        </v-card>


        <!-- Referrer Table -->
        <v-card class="table-card mt-5">

          <!-- Table Header -->
          <div class="table-header d-flex justify-space-between align-center flex-wrap ga-3">

            <div style="
                display:flex;
                align-items:center;
                gap:16px;
              ">

              <InputText v-model="filters['global'].value" placeholder="Search Referrer..." />

              <Button type="button" icon="pi pi-filter-slash" label="Clear" variant="outlined" @click="clearFilters" />

            </div>

          </div>


          <!-- Referrer Table -->
          <DataTable v-model:filters="filters" v-model:expandedRows="expandedRows" :value="refes" paginator :rows="10"
            dataKey="referrer.uuid" stripedRows class="modern-table" :rowsPerPageOptions="[5, 10, 20, 50]"
            tableStyle="min-width: 80rem" filterDisplay="menu" :loading="loading" :globalFilterFields="[
              'referrer.name',
              'referrer.email',
              'referrer.type',
              'reward_status'
            ]">

            <!-- Empty -->
            <template #empty>
              No referrers found for this campaign.
            </template>


            <!-- Loading -->
            <template #loading>
              Loading Referrers...
            </template>


            <!-- EXPAND -->
            <Column expander style="width: 60px" />


            <!-- REFERRER -->
            <Column header="Referrer">

              <template #body="slotProps">

                <div class="person-cell">

                  <div class="avatar">

                    {{ getInitials(
                      slotProps.data.referrer?.name
                    ) }}

                  </div>


                  <div>

                    <div class="person-name">

                      {{
                        slotProps.data.referrer?.name || '-'
                      }}

                    </div>

                    <div class="person-email">

                      {{
                        slotProps.data.referrer?.email || '-'
                      }}

                    </div>

                  </div>

                </div>

              </template>

            </Column>


            <!-- REFERRER TYPE -->
            <Column header="Referrer Type">

              <template #body="slotProps">

                <span class="type-pill" :class="slotProps.data.referrer?.type === 'partner'
                    ? 'partner-pill'
                    : 'user-pill'
                  ">

                  <v-icon size="14" class="mr-1">

                    {{
                      slotProps.data.referrer?.type === 'partner'
                        ? 'mdi-domain'
                        : 'mdi-account'
                    }}

                  </v-icon>

                  {{
                    formatType(
                      slotProps.data.referrer?.type
                    )
                  }}

                </span>

              </template>

            </Column>


            <!-- REFERRAL COUNT -->
            <Column header="Referrals">

              <template #body="slotProps">

                <span class="referral-count">

                  {{ slotProps.data.referral_count ?? 0 }}

                </span>

              </template>

            </Column>


            <!-- TOTAL REWARD -->
            <Column header="Total Reward">

              <template #body="slotProps">

                <strong>

                  ₦{{
                    formatAmount(
                      slotProps.data.total_reward
                    )
                  }}

                </strong>

              </template>

            </Column>


            <!-- REWARD STATUS -->
            <Column header="Reward Status">

              <template #body="slotProps">

                <span class="status-pill" :class="getStatusClass(
                  slotProps.data.reward_status
                )
                  ">

                  {{
                    formatStatus(
                      slotProps.data.reward_status
                    )
                  }}

                </span>

              </template>

            </Column>


            <!-- PAID AT -->
            <Column header="Paid At">

              <template #body="slotProps">

                <span v-if="slotProps.data.paid_at">

                  {{
                    formatDate(
                      slotProps.data.paid_at
                    )
                  }}

                </span>

                <span v-else class="muted">
                  Not paid
                </span>

              </template>

            </Column>

            <!-- ACTION -->
            <Column header="Action" style="width: 190px">

              <template #body="slotProps">

                <v-menu>

                  <template #activator="{ props }">

                    <v-btn variant="outlined" size="small" v-bind="props" :loading="statusLoadingId ===
                      slotProps.data.referrer?.uuid
                      " :disabled="['paid', 'failed'].includes(
    slotProps.data.reward_status
  )
    ">
                      {{ formatStatus(slotProps.data.reward_status) }}

                      <v-icon v-if="
                        !['paid', 'failed'].includes(
                          slotProps.data.reward_status
                        )
                      " end>
                        mdi-chevron-down
                      </v-icon>
                    </v-btn>


                  </template>


                  <v-list>
                     <!-- Paid -->
                    <v-list-item :disabled="slotProps.data.reward_status === 'paid'
                      " @click="
                        updateReferrerStatus(
                          slotProps.data,
                          'paid'
                        )
                        ">

                      <template #prepend>

                        <v-icon color="green">
                          mdi-check-circle-outline
                        </v-icon>

                      </template>

                      Mark All as Paid

                    </v-list-item>

                    <!-- Pending -->
                    <!-- <v-list-item :disabled="slotProps.data.reward_status === 'pending'
                              " @click="
                    updateReferrerStatus(
                      slotProps.data,
                      'pending'
                    )
                    ">

                      <template #prepend>

                        <v-icon color="orange">
                          mdi-clock-outline
                        </v-icon>

                      </template>

                      Pending

                    </v-list-item> -->


                    <!-- Processing -->
                    <!-- <v-list-item :disabled="slotProps.data.reward_status === 'processing'
                                " @click="
                      updateReferrerStatus(
                        slotProps.data,
                        'processing'
                      )
                      ">

                      <template #prepend>

                        <v-icon color="blue">
                          mdi-progress-clock
                        </v-icon>

                      </template>

                      Processing

                    </v-list-item> -->


                    <!-- Stopped -->
                    <!-- <v-list-item :disabled="slotProps.data.reward_status === 'stopped'
                      " @click="
                        updateReferrerStatus(
                          slotProps.data,
                          'stopped'
                        )
                        ">

                      <template #prepend>

                        <v-icon color="red">
                          mdi-stop-circle-outline
                        </v-icon>

                      </template>

                      Stop Reward

                    </v-list-item> -->


                    <!-- Failed -->
                    <v-list-item :disabled="slotProps.data.reward_status === 'failed'
                                " @click="
                      updateReferrerStatus(
                        slotProps.data,
                        'failed'
                      )
                      ">

                      <template #prepend>

                        <v-icon color="red">
                          mdi-alert-circle-outline
                        </v-icon>

                      </template>

                      Failed

                    </v-list-item>

                  </v-list>

                </v-menu>

              </template>

            </Column>


            <!-- EXPANDED ROW -->
            <template #expansion="slotProps">

              <div class="expanded-wrapper">

                <div class="expanded-header">

                  <div>

                    <h4 class="expanded-title">

                      Referral Details

                    </h4>

                    <p class="expanded-subtitle">

                      {{
                        slotProps.data.referrer?.name
                      }}
                      referred
                      {{
                        slotProps.data.referral_count
                      }}
                      user(s)

                    </p>

                  </div>


                  <div class="expanded-summary">

                    <span>
                      Total:
                    </span>

                    <strong>
                      ₦{{
                        formatAmount(
                          slotProps.data.total_reward
                        )
                      }}
                    </strong>

                  </div>

                </div>


                <!-- INNER TABLE -->
                <DataTable :value="slotProps.data.referrals || []
                  " class="inner-table" dataKey="uuid" responsiveLayout="scroll">

                  <!-- Referral Code -->
                  <Column header="Referral Code" style="width: 150px">

                    <template #body="referralProps">

                      <span class="referral-code">

                        {{
                          referralProps.data
                            .referral_code || '-'
                        }}

                      </span>

                    </template>

                  </Column>


                  <!-- Referred User -->
                  <Column header="Referred User">

                    <template #body="referralProps">

                      <div class="person-cell">

                        <div class="avatar small">

                          {{
                            getInitials(
                              referralProps.data
                                .referred_user?.name
                            )
                          }}

                        </div>


                        <div>

                          <div class="person-name">

                            {{
                              referralProps.data
                                .referred_user?.name || '-'
                            }}

                          </div>

                          <div class="person-email">

                            {{
                              referralProps.data
                                .referred_user?.email || '-'
                            }}

                          </div>

                        </div>

                      </div>

                    </template>

                  </Column>


                  <!-- Reward -->
                  <Column header="Reward">

                    <template #body="referralProps">

                      <strong>

                        ₦{{
                          formatAmount(
                            referralProps.data
                              .reward_amount
                          )
                        }}

                      </strong>

                    </template>

                  </Column>


                  <!-- Reward Status -->
                  <Column header="Status">

                    <template #body="referralProps">

                      <span class="status-pill" :class="getStatusClass(
                        referralProps.data
                          .reward_status
                      )
                        ">

                        {{
                          formatStatus(
                            referralProps.data
                              .reward_status
                          )
                        }}

                      </span>

                    </template>

                  </Column>


                  <!-- Paid At -->
                  <Column header="Paid At">

                    <template #body="referralProps">

                      <span v-if="
                        referralProps.data.paid_at
                      ">

                        {{
                          formatDate(
                            referralProps.data
                              .paid_at
                          )
                        }}

                      </span>

                      <span v-else class="muted">

                        Not paid

                      </span>

                    </template>

                  </Column>


                </DataTable>

              </div>

            </template>

          </DataTable>

        </v-card>


        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top right">

          {{ snackbar.message }}

          <template #actions>

            <v-btn variant="outlined" @click="
              snackbar.show = false
              ">
              Close
            </v-btn>

          </template>

        </v-snackbar>

      </v-container>

    </v-main>

  </v-app>

</template>


<script setup>

import {
  ref,
  computed,
  onMounted,
  onUnmounted
} from "vue"

import {
  useRoute,
  useRouter
} from "vue-router"

import AdminSidebar from "@/components/AdminSidebar.vue"
import Header from "@/components/Header.vue"

import {
  formatDate,
  getAllErrors
} from "@/util"

import {
  campaignStore
} from "@/store/campaign"

import {
  executeUpdateReferralStatus
} from "@/api/campaign"


/* --------------------------------------------------
   ROUTER
-------------------------------------------------- */

const route = useRoute()
const router = useRouter()


/* --------------------------------------------------
   SIDEBAR
-------------------------------------------------- */

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)


const toggleSidebar = () => {

  if (isMobile.value) {

    drawerOpen.value =
      !drawerOpen.value

  } else {

    isSidebarCollapsed.value =
      !isSidebarCollapsed.value

  }

}


const checkMobile = () => {

  isMobile.value =
    window.innerWidth < 768

  drawerOpen.value =
    !isMobile.value

}


/* --------------------------------------------------
   DATA
-------------------------------------------------- */

const loading = ref(false)

const campaign = ref(null)

const statusLoadingId = ref(null)

const refStore =
  campaignStore()

const campaignUuid =
  route.params.uuid


const refes =
  computed(() =>
    refStore.referrers || []
  )


/* --------------------------------------------------
   EXPANDED ROWS
-------------------------------------------------- */

const expandedRows =
  ref({})


/* --------------------------------------------------
   FILTER
-------------------------------------------------- */

const filters = ref({

  global: {

    value: null,

    matchMode: "contains"

  }

})


const clearFilters = () => {

  filters.value = {

    global: {

      value: null,

      matchMode: "contains"

    }

  }

}


/* --------------------------------------------------
   TOTALS
-------------------------------------------------- */

const totalReferrals = computed(() => {

  return refes.value.reduce(
    (total, referrer) =>
      total +
      Number(
        referrer.referral_count || 0
      ),
    0
  )

})


const totalReward = computed(() => {

  return refes.value.reduce(
    (total, referrer) =>
      total +
      Number(
        referrer.total_reward || 0
      ),
    0
  )

})


/* --------------------------------------------------
   UPDATE REFERRER STATUS
-------------------------------------------------- */

const updateReferrerStatus = async (
  referrer,
  status
) => {

  if (
    referrer.reward_status === status
  ) {

    return

  }


  if (status === "paid") {

    const confirmed =
      window.confirm(
        `Are you sure you want to mark ₦${formatAmount(
          referrer.total_reward
        )} as PAID for ${referrer.referrer?.name || "this referrer"
        }?`
      )

    if (!confirmed) {

      return

    }

  }


  statusLoadingId.value =
    referrer.referrer?.uuid


  try {

    /*
     * IMPORTANT:
     *
     * The backend endpoint currently
     * expects the REFERRAL UUID.
     *
     * Since the outer referrer contains
     * multiple referrals, we update all
     * referrals belonging to this referrer.
     */

    const referrals =
      referrer.referrals || []


    for (const referral of referrals) {

      await executeUpdateReferralStatus(
        referral.uuid,
        status
      )

    }


    /*
     * Update outer row
     */

    referrer.reward_status =
      status


    if (status === "paid") {

      referrer.paid_at =
        new Date().toISOString()

    } else {

      referrer.paid_at = null

    }


    /*
     * Update inner rows
     */

    referrals.forEach(
      referral => {

        referral.reward_status =
          status

        if (status === "paid") {

          referral.paid_at =
            new Date().toISOString()

        } else {

          referral.paid_at = null

        }

      }
    )


    showSnackbar(
      status === "paid"
        ? `₦${formatAmount(
          referrer.total_reward
        )} marked as paid`
        : `Reward marked as ${formatStatus(
          status
        )}`
    )


  } catch (error) {

    console.error(error)

    showSnackbar(
      getAllErrors(error) ||
      "Unable to update reward status",
      "error"
    )

  } finally {

    statusLoadingId.value =
      null

  }

}


/* --------------------------------------------------
   HELPERS
-------------------------------------------------- */

const formatType = (type) => {

  if (!type)
    return "-"

  return type
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      char =>
        char.toUpperCase()
    )

}


const formatStatus = (status) => {

  if (!status)
    return "Unknown"

  return status
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      char =>
        char.toUpperCase()
    )

}


const getStatusClass = (status) => {

  switch (status) {

    case "paid":
      return "status-paid"

    case "processing":
      return "status-processing"

    case "pending":
      return "status-pending"

    case "stopped":
      return "status-stopped"

    case "failed":
      return "status-failed"

    default:
      return "status-pending"

  }

}


const formatAmount = (amount) => {

  if (
    amount === null ||
    amount === undefined
  ) {

    return "0.00"

  }

  return Number(amount)
    .toLocaleString(
      "en-NG",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    )

}


const getInitials = (name) => {

  if (!name)
    return "?"

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(
      word =>
        word
          .charAt(0)
          .toUpperCase()
    )
    .join("")

}


const goBack = () => {

  router.back()

}


/* --------------------------------------------------
   SNACKBAR
-------------------------------------------------- */

const snackbar = ref({

  show: false,

  message: "",

  color: "success"

})


const showSnackbar = (
  message,
  color = "success"
) => {

  snackbar.value.message =
    message

  snackbar.value.color =
    color

  snackbar.value.show =
    true

}


/* --------------------------------------------------
   LIFECYCLE
-------------------------------------------------- */

onMounted(async () => {

  checkMobile()

  window.addEventListener(
    "resize",
    checkMobile
  )


  try {

    loading.value = true

    await refStore.showReferrers(
      campaignUuid
    )


    /*
     * Your API response contains
     * campaign outside the referrer
     * collection.
     *
     * Depending on your store implementation,
     * populate campaign here if available.
     */

    if (refStore.campaign) {

      campaign.value =
        refStore.campaign

    }

  } catch (error) {

    console.error(error)

    showSnackbar(
      getAllErrors(error) ||
      "Unable to load campaign referrers",
      "error"
    )

  } finally {

    loading.value = false

  }

})


onUnmounted(() => {

  window.removeEventListener(
    "resize",
    checkMobile
  )

})

</script>


<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}


.main-content {
  flex: 1;
  background: #f8fafc;
}


/* --------------------------------------------------
   PAGE HEADER
-------------------------------------------------- */

.page-header {
  display: flex;
  align-items: center;
}


.page-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
}


.page-subtitle {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 14px;
}


/* --------------------------------------------------
   SUMMARY
-------------------------------------------------- */

.summary-card {

  padding: 20px;

  border-radius: 14px;

  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.05);

  display: flex;

  justify-content: space-between;

  align-items: center;

  gap: 20px;

}


.summary-left {

  display: flex;

  align-items: center;

  gap: 14px;

}


.summary-stats {

  display: flex;

  gap: 35px;

}


.summary-stat {

  display: flex;

  flex-direction: column;

}


.summary-label {

  color: #64748b;

  font-size: 12px;

  margin-bottom: 4px;

}


.summary-stat strong {

  font-size: 18px;

}


/* --------------------------------------------------
   CAMPAIGN
-------------------------------------------------- */

.campaign-image-wrapper {

  width: 52px;

  height: 52px;

  flex-shrink: 0;

}


.campaign-image {

  width: 52px;

  height: 52px;

  border-radius: 10px;

  object-fit: cover;

}


.campaign-image-placeholder {

  width: 52px;

  height: 52px;

  border-radius: 10px;

  background: #eef7f0;

  color: #065f46;

  display: flex;

  align-items: center;

  justify-content: center;

}


.campaign-name {

  font-size: 16px;

  font-weight: 700;

}


.campaign-description {

  color: #64748b;

  font-size: 13px;

  margin-top: 3px;

}


/* --------------------------------------------------
   TABLE
-------------------------------------------------- */

.table-card {

  padding: 20px;

  border-radius: 14px;

  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.05);

}


.table-header {

  display: flex;

  justify-content: space-between;

  align-items: center;

  margin-bottom: 20px;

}


.modern-table {
  border: none;
}


:deep(.modern-table .p-datatable-thead > tr > th) {

  background: #eef7f0;

  color: #065f46;

  font-weight: 600;

  border-bottom:
    2px solid #c1edc3;

}


/* --------------------------------------------------
   PEOPLE
-------------------------------------------------- */

.person-cell {

  display: flex;

  align-items: center;

  gap: 10px;

}


.avatar {

  width: 38px;

  height: 38px;

  border-radius: 50%;

  background: #eef7f0;

  color: #065f46;

  display: flex;

  align-items: center;

  justify-content: center;

  font-weight: 700;

  font-size: 13px;

  flex-shrink: 0;

}


.avatar.small {

  width: 32px;

  height: 32px;

  font-size: 11px;

}


.person-name {

  font-weight: 600;
}


.person-email {

  font-size: 12px;

  color: #64748b;

}


/* --------------------------------------------------
   REFERRAL COUNT
-------------------------------------------------- */

.referral-count {

  font-weight: 700;

  color: #065f46;

}


/* --------------------------------------------------
   REFERRAL CODE
-------------------------------------------------- */

.referral-code {

  padding: 5px 8px;

  border-radius: 5px;

  background: #f1f5f9;

  color: #475569;

  font-size: 12px;

  font-weight: 600;

  font-family: monospace;

}


/* --------------------------------------------------
   TYPE
-------------------------------------------------- */

.type-pill {

  display: inline-flex;

  align-items: center;

  padding: 5px 10px;

  border-radius: 999px;

  font-size: 12px;

  font-weight: 600;

}


.user-pill {

  background: #eff6ff;

  color: #2563eb;

}


.partner-pill {

  background: #f5f3ff;

  color: #7c3aed;

}


/* --------------------------------------------------
   STATUS
-------------------------------------------------- */

.status-pill {

  display: inline-block;

  padding: 5px 10px;

  border-radius: 999px;

  font-size: 12px;

  font-weight: 600;

}


.status-paid {

  background: #ecfdf5;

  color: #16a34a;

}


.status-processing {

  background: #eff6ff;

  color: #2563eb;

}


.status-pending {

  background: #fff7ed;

  color: #ea580c;

}


.status-stopped {

  background: #fef2f2;

  color: #dc2626;

}


.status-failed {

  background: #fef2f2;

  color: #dc2626;

}


.muted {

  color: #94a3b8;

  font-size: 13px;

}


/* --------------------------------------------------
   EXPANDED ROW
-------------------------------------------------- */

.expanded-wrapper {

  padding: 20px 30px;

  background: #f8fafc;

  border-top: 1px solid #e2e8f0;

}


.expanded-header {

  display: flex;

  justify-content: space-between;

  align-items: center;

  margin-bottom: 15px;

}


.expanded-title {

  margin: 0;

  font-size: 15px;

  font-weight: 700;

}


.expanded-subtitle {

  margin: 4px 0 0;

  font-size: 12px;

  color: #64748b;

}


.expanded-summary {

  display: flex;

  align-items: center;

  gap: 8px;

  font-size: 13px;

  color: #64748b;

}


.expanded-summary strong {

  color: #111827;

  font-size: 15px;

}


/* --------------------------------------------------
   INNER TABLE
-------------------------------------------------- */

.inner-table {

  border-radius: 8px;

  overflow: hidden;

}


:deep(.inner-table .p-datatable-thead > tr > th) {

  background: #ffffff;

  color: #475569;

  font-size: 12px;

  font-weight: 600;

}


:deep(.inner-table .p-datatable-tbody > tr > td) {

  background: #ffffff;

  font-size: 13px;

}


/* --------------------------------------------------
   BUTTON
-------------------------------------------------- */

.theme-button {

  height: 46px;

  background: #000;

  color: #fff;

  border-radius: 5px;

  font-weight: 600;

}


.p-datatable-tbody>tr {

  cursor: pointer;

}


.p-datatable-tbody>tr:hover {

  background-color: #f5f5f5;

}


/* --------------------------------------------------
   MOBILE
-------------------------------------------------- */

@media (max-width: 768px) {

  .summary-card {

    flex-direction: column;

    align-items: flex-start;

  }


  .summary-stats {

    width: 100%;

    justify-content: space-between;

    gap: 15px;

  }


  .summary-stat strong {

    font-size: 16px;

  }


  .expanded-wrapper {

    padding: 15px;

  }

}
</style>